import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Box, makeStyles, Typography } from 'housecall-ui';
import Button from 'housecall-ui/dist/DesignSystem/Inputs/Buttons/Button';

import { ConfirmBookingPayload, LeadSource, PaymentConfig } from '../api/api';
import ConfirmationSummary from '../components/ConfirmationSummary';
import { mapPriceFormCartItemsToConfirmPayload } from '../components/form/utils';
import { Layout } from '../components/Layout';
import { ScrollableContent } from '../components/ScrollableContent';
import { Spinner } from '../components/Spinner';
import { StepProgressBar } from '../components/StepProgressBar';
import { Codeowners } from '../consts/codeowners';
import { useBookingConfirmationMutation } from '../hooks/useBookingConfirmationMutation';
import { useChangeStep } from '../hooks/useChangeStep';
import { trackButtonClicked, trackPage, useAnalyticsHomeownerParams } from '../hooks/useEventTracking';
import { useOrgData } from '../hooks/useOrgData';
import { useRedirectAfterConfirmation } from '../hooks/useRedirectAfterConfirmation';
import { AmplitudeFlagKeys } from '../resources/amplitude/amplitudeFlags';
import useIsExperimentActive from '../resources/amplitude/useIsExperimentActive';
import trackGoogleReserveConversion, { isFromGoogle } from '../resources/google/trackGoogleReserveConversion';
import { useRecaptcha } from '../resources/google/useRecaptcha';
import cacheQueryKeys from '../resources/queryCacheKeys';
import { ServiceCartItem } from '../state/types';
import { useOnlineBookingReducer } from '../state/useOnlineBookingReducer';
import { getItemFromCart, getPricingFormItemsFromCart, hasPaymentStep, isServiceCartItem } from '../state/utils';
import { useToastController } from '../toast';
import { captureMessage, statusCodesMapper } from '../utils/exception';
import { mergeDescription } from '../utils/mergeDescription';

import { BackArrowButton } from './BackArrowButton';

const CONFIRM_BOOKING_ACTION = 'confirm_booking';
const RECAPTCHA_ALERT_MESSAGE =
  'Your browser is blocking our security verification tool. Please use another browser or disable your browser extensions and refresh the page.';

const BUTTON_LABEL_MAP = {
  no_payment: 'Book my appointment',
  full_payment: 'Confirm booking & pay',
  deposit: 'Confirm booking & pay deposit',
};

const useStyles = makeStyles(() => ({
  container: {
    overflowWrap: 'break-word',
  },
}));

const getButtonLabel = (requiresPayment: boolean, config?: PaymentConfig) => {
  if (!requiresPayment) return BUTTON_LABEL_MAP.no_payment;

  if (config) {
    return BUTTON_LABEL_MAP[config];
  }

  return BUTTON_LABEL_MAP.full_payment;
};

export const ConfirmBookingStep = () => {
  const styles = useStyles();
  const { state, dispatch, actions } = useOnlineBookingReducer();
  const {
    token,
    alert,
    contactDetails,
    service,
    description,
    arrivalWindow,
    question1,
    question2,
    cart,
    photos,
    initParams,
  } = state;
  const isPreview = initParams?.source === 'preview';
  const { getRecaptchaToken } = useRecaptcha();
  const queryParams = new URLSearchParams(window.location.search);
  const attr = queryParams.get('attr') || '';
  const refferalToken = queryParams.get('ref') || null;
  const isLeadSourceGoogle = isFromGoogle();
  const { mutateAsync, isLoading: mutationLoading } = useBookingConfirmationMutation();
  const { isUnscheduled, data: orgData } = useOrgData({ token });
  const trackingProps = useAnalyticsHomeownerParams();
  const { showToast } = useToastController();
  const { redirect, hasRedirectUrl } = useRedirectAfterConfirmation();
  const canHandleOnlineBookingUnavailable = useIsExperimentActive(AmplitudeFlagKeys.dontServeCustomers);
  const queryClient = useQueryClient();
  const { changeStep } = useChangeStep();

  const requiresPayment = Boolean(orgData && hasPaymentStep(cart, orgData, state.tripCharge));

  const handleNextClick = async () => {
    if (isPreview) {
      if (requiresPayment) {
        changeStep('payment');
      } else {
        dispatch(actions.setSuccess());
      }
      return;
    }

    trackButtonClicked({
      buttonName: requiresPayment ? 'ob_confirm_order_and_pay' : 'ob_confirm_booking',
      ...trackingProps,
    });
    const services = cart.filter(isServiceCartItem).map((cartItem) => ({
      uuid: getItemFromCart(cartItem).uuid || '',
      quantity: (cartItem as ServiceCartItem).quantity,
    }));

    const priceForms = getPricingFormItemsFromCart(state).map(mapPriceFormCartItemsToConfirmPayload);

    const cartDescription = mergeDescription(cart);
    const responseToken = await getRecaptchaToken(CONFIRM_BOOKING_ACTION);
    if (!responseToken && initParams?.source !== 'customer_portal') {
      dispatch(actions.setAlert(RECAPTCHA_ALERT_MESSAGE));
      captureMessage('Online booking recaptcha error', Codeowners.CUSTOMER_INTAKE);
      return;
    }

    const isCustomerPortalSource = initParams?.source === 'customer_portal';
    const existingCustomerParams = isCustomerPortalSource
      ? ({
          customerUuid: initParams.customerUuid,
          customerPortalToken: initParams.token,
          source: initParams.source,
        } as const)
      : {};

    const payload: ConfirmBookingPayload = {
      service,
      token,
      description: description || cartDescription,
      contactDetails,
      arrivalWindow,
      question1: question1 ? `\n${service?.question1} \n- ${question1}` : '',
      question2: question2 ? `\n${service?.question2} \n- ${question2}` : '',
      services,
      priceForms,
      leadSource: isLeadSourceGoogle ? LeadSource.RESERVE_WITH_GOOGLE : LeadSource.DEFAULT,
      responseToken,
      attr,
      ...existingCustomerParams,
    };

    if (refferalToken) {
      payload.ref = refferalToken;
    }

    payload.attachments = photos;

    if (isUnscheduled) {
      payload.type = 'unscheduled';
    }

    const response = await mutateAsync(payload);

    statusCodesMapper(response, {
      unhandled: () => {
        showToast('Failed to confirm booking, please try again.');
      },
      serviceNotFound: () => {
        dispatch(actions.setWarningMessage('Selected service is no longer available. Please choose a new service.'));
        dispatch(actions.resetFlowWithCart());
      },
      slotUnavailable: () => {
        queryClient.resetQueries([cacheQueryKeys.OnlineBookingTimeSlots]);
        dispatch(actions.resetArrival());
        dispatch(actions.setWarningMessage('Selected time slot is no longer available. Please choose a  new time.'));
        changeStep('booking');
      },
      onlineBookingUnavailable: () => {
        if (canHandleOnlineBookingUnavailable) {
          dispatch(actions.setCustomerBannedFlow());
        } else {
          showToast('Failed to confirm booking, please try again.');
        }
      },
      success: () => {
        if (response.data?.paymentRequired) {
          changeStep('payment');
        } else {
          trackPage('Online Booking - success view', {
            ...trackingProps,
            path: `ob_successfully_booked`,
          });
          trackGoogleReserveConversion();

          if (hasRedirectUrl) {
            redirect();
          } else {
            dispatch(actions.setSuccess());
          }
        }
      },
    });
  };

  const handleBackClick = () => {
    changeStep(isUnscheduled ? 'contact-details' : 'booking');
  };

  return (
    <Layout
      Header={<StepProgressBar currentStep={5} stepCount={5} />}
      ContentComponent={
        <>
          <BackArrowButton onClick={handleBackClick} />
          <Typography variant="h5">Booking confirmation</Typography>
          <ScrollableContent>
            <Box my={2} className={styles.container}>
              <ConfirmationSummary />
            </Box>
          </ScrollableContent>
          {alert ? (
            <Box py={2}>
              <Alert severity="error" role="status">
                {alert}
              </Alert>
            </Box>
          ) : null}
          <Spinner show={mutationLoading} />
        </>
      }
      Buttons={
        <Button version="v2" size="medium" fullWidth variant="contained" color="secondary" onClick={handleNextClick}>
          {getButtonLabel(requiresPayment, orgData?.paymentSettings?.config)}
        </Button>
      }
    />
  );
};
